// Review / edit screen after AI estimates a meal. Designed to be visually
// clean and Carb-Manager-grade, with the wheel picker as the primary
// quantity control. Macros editable inline; portion editing is gone (users
// pick units from the wheel, never type them).
//
// Layout (top → bottom):
//   1. Goal-aware totals card — donut for calories vs daily remaining +
//      4-row macro grid showing "of N left" against the day's goal
//   2. Meal-type segmented control (Breakfast / Lunch / Dinner / Snack)
//   3. Optional banners (disagreement, clarifying questions w/ re-estimate)
//   4. Items accordion — only ONE item expanded at a time. Collapsed rows
//      show name + portion + macros in a single tappable strip.
//   5. + Add item (dashed full-width)
//   6. Sticky bottom action bar (Cancel | Save · N kcal)

import { useMemo, useState } from "react";
import { ChevronDown, Pencil } from "lucide-react";
import MealTypePicker from "./MealTypePicker";
import PromptModal from "./PromptModal";
import FeedbackModal from "./FeedbackModal";
import WheelPicker, { type WheelColumn } from "./WheelPicker";
import type { MealEstimate, FoodItem, MealType } from "../lib/types";
import type { Goals } from "../lib/goals";
import {
  densityFor,
  formatPortion,
  fromGrams,
  guessFoodCategory,
  parseGramsFromPortion,
  parsePortion,
  scalePortionText,
  toGrams,
  unitConversionFactor,
  type CanonicalUnit
} from "../lib/portions";

interface Props {
  initial: MealEstimate;
  initialMealType: MealType;
  /** Used to surface the "models disagree" banner; pass 0 for single-source paths. */
  disagreementPct: number;
  /** Optional — when provided, the totals card shows "of N left" against daily targets. */
  goals?: Goals;
  /** Optional — what's already eaten today before this meal. Lets us compute "remaining". */
  alreadyEatenToday?: MealEstimate["totals"];
  /** True while the parent is persisting the meal — disables Save + shows "Saving…". */
  saving?: boolean;
  /** Error message from the parent's save attempt. Shown above the totals card. */
  saveError?: string | null;
  onConfirm: (final: MealEstimate, mealType: MealType) => void;
  onCancel: () => void;
  /** Called when user submits extra info to re-estimate. If omitted, refine UI is hidden. */
  onRefine?: (additionalInfo: string) => void;
}

const QUICK_QTYS = [0.5, 1, 1.5, 2, 3];

// Integer values for the quantity wheel (count-based units).
const INT_VALUES: number[] = Array.from({ length: 21 }, (_, i) => i); // 0..20
// Stable key array for the fraction wheel — IMPORTANT: keep this as a
// module-level constant so it's the same reference across renders, otherwise
// WheelPicker's sync useEffect re-fires and causes feedback loops.
const FRACTION_VALUES: Array<{ key: string; label: string; value: number }> = [
  { key: "0", label: "—", value: 0 },
  { key: "0.125", label: "⅛", value: 0.125 },
  { key: "0.25", label: "¼", value: 0.25 },
  { key: "0.333", label: "⅓", value: 1 / 3 },
  { key: "0.5", label: "½", value: 0.5 },
  { key: "0.667", label: "⅔", value: 2 / 3 },
  { key: "0.75", label: "¾", value: 0.75 }
];
const FRACTION_KEYS: string[] = FRACTION_VALUES.map((f) => f.key);

// Mass / volume units use a denser scalar wheel.
// 1g granularity from 1-50, 5g from 55-200, 10g from 210-500.
// This way "1 g" is showable AND "200 g" doesn't take 200 wheel rows.
const MASS_VALUES: number[] = [
  ...Array.from({ length: 50 }, (_, i) => i + 1), // 1..50
  ...Array.from({ length: 30 }, (_, i) => 55 + i * 5), // 55..200
  ...Array.from({ length: 30 }, (_, i) => 210 + i * 10) // 210..500
];

// The actual wheel options shown for any given item are filtered to its
// category via getCompatibleUnits() — otherwise we'd let users do lossy
// cross-category switches (e.g. serving → tbsp) that have no honest
// auto-conversion. UNIT_LABELS still covers all canonical units so any
// render path can label them.

const UNIT_LABELS: Record<CanonicalUnit, string> = {
  g: "g",
  oz: "oz",
  ml: "ml",
  "fl-oz": "fl oz",
  cup: "cup",
  tbsp: "tbsp",
  tsp: "tsp",
  slice: "slice",
  piece: "piece",
  serving: "serving"
};

const VOLUME_UNITS: CanonicalUnit[] = ["ml", "fl-oz", "tsp", "tbsp", "cup"];
const MASS_UNITS: CanonicalUnit[] = ["g", "oz"];

/**
 * Return the units the wheel should offer for a given item.
 *
 * Decision tree:
 *  1. If the item has `grams_per_portion` (set by AI or generic-foods
 *     catalog) — offer EVERY unit. With grams as the canonical middle,
 *     toGrams() / fromGrams() can convert across categories using the
 *     stored density (food_category → DENSITY_G_PER_ML).
 *  2. Otherwise fall back to same-category-only:
 *     - Volume parsed → all volume units (cup ↔ tbsp ↔ tsp ↔ ml ↔ fl oz)
 *     - Mass parsed   → all mass units (g ↔ oz)
 *     - Count parsed  → only the parsed unit itself
 *
 * Why the dual-mode? Items logged before grams_per_portion existed (or
 * AI calls that returned without it) shouldn't get a wheel that offers
 * options we can't honor. Items WITH grams data get the full freedom.
 */
function getCompatibleUnits(
  unit: CanonicalUnit,
  hasGrams: boolean
): CanonicalUnit[] {
  if (hasGrams) {
    // Full conversion freedom — toGrams/fromGrams handles every transition.
    // For count units we include only the ORIGINAL parsed count unit (not
    // all of serving/piece/slice) — they'd all alias to "1 of the original
    // food" anyway, so offering multiple is just visual noise.
    const units: CanonicalUnit[] = ["g", "oz", "ml", "fl-oz", "tsp", "tbsp", "cup"];
    if (!units.includes(unit)) units.push(unit);
    return units;
  }
  if (VOLUME_UNITS.includes(unit)) return VOLUME_UNITS;
  if (MASS_UNITS.includes(unit)) return MASS_UNITS;
  // Count-based with no grams data — no safe conversions.
  return [unit];
}

function isMassOrVolumeUnit(u: CanonicalUnit): boolean {
  return u === "g" || u === "ml";
}

export default function MealReview({
  initial,
  initialMealType,
  disagreementPct,
  goals,
  alreadyEatenToday,
  saving = false,
  saveError = null,
  onConfirm,
  onCancel,
  onRefine
}: Props) {
  const [items, setItems] = useState<FoodItem[]>(initial.items);
  const [quantities, setQuantities] = useState<number[]>(initial.items.map(() => 1));
  const [mealType, setMealType] = useState<MealType>(initialMealType);
  const [refinement, setRefinement] = useState("");
  // Accordion: which item index is currently expanded (null = none).
  const [expandedIdx, setExpandedIdx] = useState<number | null>(
    initial.items.length > 0 ? 0 : null
  );

  function updateItem(idx: number, patch: Partial<FoodItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function updateQuantity(idx: number, newQ: number) {
    if (!Number.isFinite(newQ) || newQ <= 0) return;
    const oldQ = quantities[idx];
    const ratio = newQ / oldQ;
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              calories: round1(it.calories * ratio),
              protein_g: round1(it.protein_g * ratio),
              carbs_g: round1(it.carbs_g * ratio),
              fat_g: round1(it.fat_g * ratio),
              fiber_g: it.fiber_g != null ? round1(it.fiber_g * ratio) : undefined
            }
          : it
      )
    );
    setQuantities((prev) => prev.map((q, i) => (i === idx ? newQ : q)));
  }

  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setQuantities((prev) => prev.filter((_, i) => i !== idx));
    setExpandedIdx((prev) => {
      if (prev == null) return null;
      if (prev === idx) return null;
      if (prev > idx) return prev - 1;
      return prev;
    });
  }

  function addItem() {
    setItems((prev) => [
      ...prev,
      { name: "New item", portion: "1 serving", calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }
    ]);
    setQuantities((prev) => [...prev, 1]);
    setExpandedIdx(items.length); // expand the new one
  }

  const totals = useMemo(
    () =>
      items.reduce(
        (a, i) => ({
          calories: a.calories + i.calories,
          protein_g: a.protein_g + i.protein_g,
          carbs_g: a.carbs_g + i.carbs_g,
          fat_g: a.fat_g + i.fat_g,
          fiber_g: a.fiber_g + (i.fiber_g ?? 0)
        }),
        { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 }
      ),
    [items]
  );

  function confirm() {
    // Bake the per-item quantity into the portion text so the saved meal
    // accurately reflects what was logged. Macros are already scaled in
    // `items`; this keeps the portion string in sync. Uses the shared
    // scalePortionText helper so this matches the quick-log path exactly.
    const finalItems = items.map((it, idx) => {
      const q = quantities[idx];
      if (q === 1) return it;
      return { ...it, portion: scalePortionText(it.portion, q) };
    });
    const finalTotals = finalItems.reduce(
      (a, i) => ({
        calories: a.calories + i.calories,
        protein_g: a.protein_g + i.protein_g,
        carbs_g: a.carbs_g + i.carbs_g,
        fat_g: a.fat_g + i.fat_g,
        fiber_g: a.fiber_g + (i.fiber_g ?? 0)
      }),
      { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 }
    );
    onConfirm({ ...initial, items: finalItems, totals: finalTotals }, mealType);
  }

  const showDisagreementBanner = disagreementPct > 25;
  const showLowConfBanner = initial.confidence === "low";

  return (
    <div className="p-4 space-y-4 pb-28">
      {/* Save error — sits above the totals so it's the first thing the user
          sees after a failed save. Kept whitespace-pre-line so the multi-line
          message from the parent renders properly. */}
      {saveError && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm whitespace-pre-line">
          {saveError}
        </div>
      )}

      {/* 1. Goal-aware totals card */}
      <TotalsCard totals={totals} goals={goals} alreadyEaten={alreadyEatenToday} />

      {/* 2. Meal type */}
      <MealTypePicker value={mealType} onChange={setMealType} />

      {/* 3a. Disagreement banner */}
      {showDisagreementBanner && (
        <div className="p-3 rounded-lg bg-amber-950 border border-amber-800 text-amber-200 text-sm">
          The AI is uncertain on portion sizes — double-check before saving.
          {items.length > 0 && (
            <button
              type="button"
              onClick={() => setExpandedIdx(0)}
              className="block mt-1.5 text-amber-300 underline text-xs"
            >
              Open the first item to adjust →
            </button>
          )}
        </div>
      )}

      {/* 3b. Clarifying questions + re-estimate input */}
      {initial.clarifying_questions.length > 0 && (
        <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 text-sm space-y-3">
          <div>
            <div className="text-slate-400 mb-1">
              {showLowConfBanner ? "Helpful to know:" : "AI is unsure about:"}
            </div>
            <ul className="list-disc pl-5 space-y-1 text-slate-200">
              {initial.clarifying_questions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </div>
          {onRefine && (
            <div className="pt-2 border-t border-slate-800 space-y-2">
              <textarea
                value={refinement}
                onChange={(e) => setRefinement(e.target.value)}
                placeholder="Answer the questions above — e.g. 'medium brownie, made with oil'"
                rows={2}
                className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:border-emerald-500"
              />
              <button
                type="button"
                onClick={() => {
                  if (refinement.trim()) {
                    onRefine(refinement.trim());
                    setRefinement("");
                  }
                }}
                disabled={!refinement.trim()}
                className="w-full py-2 rounded bg-emerald-500 text-slate-950 text-sm font-medium disabled:opacity-50"
              >
                Re-estimate with this info
              </button>
            </div>
          )}
        </div>
      )}

      {/* 4. Items accordion */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <div className="text-xs text-slate-400 uppercase tracking-wide">
            {items.length === 1 ? "1 item" : `${items.length} items`}
          </div>
          <div className="text-[11px] text-slate-500">
            Tap a row to adjust amount or macros
          </div>
        </div>

        {items.map((item, idx) => (
          <ItemAccordion
            key={idx}
            item={item}
            multiplier={quantities[idx]}
            expanded={expandedIdx === idx}
            onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            onUpdateItem={(patch) => updateItem(idx, patch)}
            onUpdateMultiplier={(m) => updateQuantity(idx, m)}
            onRemove={() => removeItem(idx)}
          />
        ))}

        <button
          type="button"
          onClick={addItem}
          className="w-full py-3 rounded-2xl border border-dashed border-slate-700 text-sm text-slate-400 hover:text-emerald-400 hover:border-emerald-700"
        >
          + Add item
        </button>
      </div>

      {/* Sticky bottom action bar */}
      <div
        className="fixed bottom-0 inset-x-0 p-3 bg-slate-950/95 backdrop-blur border-t border-slate-800 z-30"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-md md:max-w-2xl lg:max-w-3xl mx-auto flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving}
            className="flex-[2] py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : `Save · ${Math.round(totals.calories)} kcal`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TotalsCard — calories donut + macro grid with "of N left" against daily goal
// ----------------------------------------------------------------------------

function TotalsCard({
  totals,
  goals,
  alreadyEaten
}: {
  totals: MealEstimate["totals"];
  goals?: Goals;
  alreadyEaten?: MealEstimate["totals"];
}) {
  // If we have goals + what's already eaten today, show "of N left".
  // Otherwise just show the meal totals.
  const hasGoals = goals !== undefined;
  const eaten = alreadyEaten ?? { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 };

  // Net-carbs preference: when on, the "Carbs" row label + values reflect
  // net carbs (total carbs - fiber) instead of total carbs. Carb goal
  // interpretation changes but the goal number stays the same — common
  // keto/low-carb framing.
  const useNetCarbs = goals?.track_net_carbs === true;
  const fiberGoal = goals?.fiber_g_goal ?? 30;
  const eatenCarbsAdjusted = useNetCarbs
    ? Math.max(0, eaten.carbs_g - (eaten.fiber_g ?? 0))
    : eaten.carbs_g;
  const totalsCarbsAdjusted = useNetCarbs
    ? Math.max(0, totals.carbs_g - (totals.fiber_g ?? 0))
    : totals.carbs_g;
  const totalsFiber = totals.fiber_g ?? 0;

  const remaining = hasGoals
    ? {
        calories: Math.max(0, goals!.calories - eaten.calories),
        protein_g: Math.max(0, goals!.protein_g - eaten.protein_g),
        carbs_g: Math.max(0, goals!.carbs_g - eatenCarbsAdjusted),
        fat_g: Math.max(0, goals!.fat_g - eaten.fat_g),
        fiber_g: Math.max(0, fiberGoal - (eaten.fiber_g ?? 0))
      }
    : null;

  // Donut fill — fraction of remaining (or fraction of total if no goal).
  const donutFraction = remaining
    ? Math.min(1, totals.calories / Math.max(remaining.calories, 1))
    : 0;
  const donutCircumference = 2 * Math.PI * 36;
  const donutDashoffset = donutCircumference * (1 - donutFraction);

  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-4">
      {/* Donut */}
      <div className="relative w-24 h-24 flex-shrink-0">
        <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
          <circle cx="40" cy="40" r="36" fill="none" stroke="#1e293b" strokeWidth="8" />
          <circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke="#34d399"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={donutCircumference}
            strokeDashoffset={donutDashoffset}
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xl font-bold text-slate-100 tabular-nums leading-none">
            {Math.round(totals.calories)}
          </div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">kcal</div>
        </div>
      </div>

      {/* Macro grid */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <MacroRow
          label="Cal"
          eaten={totals.calories}
          remaining={remaining?.calories}
          unit=""
          color="emerald"
        />
        <MacroRow
          label="Protein"
          eaten={totals.protein_g}
          remaining={remaining?.protein_g}
          unit="g"
          color="sky"
        />
        <MacroRow
          label={useNetCarbs ? "Net carbs" : "Carbs"}
          eaten={totalsCarbsAdjusted}
          remaining={remaining?.carbs_g}
          unit="g"
          color="amber"
        />
        <MacroRow
          label="Fat"
          eaten={totals.fat_g}
          remaining={remaining?.fat_g}
          unit="g"
          color="violet"
        />
        <MacroRow
          label="Fiber"
          eaten={totalsFiber}
          remaining={remaining?.fiber_g}
          unit="g"
          color="emerald"
        />
      </div>
    </section>
  );
}

function MacroRow({
  label,
  eaten,
  remaining,
  unit,
  color
}: {
  label: string;
  eaten: number;
  remaining: number | undefined;
  unit: string;
  color: "emerald" | "sky" | "amber" | "violet";
}) {
  const colorClass = {
    emerald: "text-emerald-400",
    sky: "text-sky-400",
    amber: "text-amber-400",
    violet: "text-violet-400"
  }[color];

  const isOver = remaining !== undefined && eaten > remaining;
  const remainingDisplay = remaining === undefined
    ? null
    : isOver
    ? `−${Math.round(eaten - remaining)}${unit} over`
    : `${Math.round(remaining - eaten)}${unit} left`;

  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className={`${colorClass} font-medium w-14 flex-shrink-0`}>{label}</span>
      <span className="text-slate-200 tabular-nums">
        {Math.round(eaten)}
        {unit}
      </span>
      <span className={`tabular-nums text-right ${isOver ? "text-rose-400" : "text-slate-500"}`}>
        {remainingDisplay ?? ""}
      </span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// ItemAccordion — single row that expands into the wheel picker + macros panel
// ----------------------------------------------------------------------------

interface ItemAccordionProps {
  item: FoodItem;
  multiplier: number;
  expanded: boolean;
  onToggle: () => void;
  onUpdateItem: (patch: Partial<FoodItem>) => void;
  onUpdateMultiplier: (newMultiplier: number) => void;
  onRemove: () => void;
}

function ItemAccordion({
  item,
  multiplier,
  expanded,
  onToggle,
  onUpdateItem,
  onUpdateMultiplier,
  onRemove
}: ItemAccordionProps) {
  // "Custom portion…" escape hatch — opens PromptModal for free-form
  // portion entry ("100g", "1 tbsp", "half container"). When the wheel
  // doesn't expose what the user wants, this is the bypass. Macros stay
  // pinned (no auto-rescale) because arbitrary strings can't always be
  // converted — the user is signing up for manual macro entry below.
  const [showCustomPortion, setShowCustomPortion] = useState(false);

  // "Report incorrect" — opens FeedbackModal with the item details pre-
  // filled. Until a real community-edit flow ships, this is how users
  // signal "this AI estimate is wrong" — feedback emails the founder.
  const [showReport, setShowReport] = useState(false);

  // Parse the item's portion string into qty + unit. Fall back to (1, serving)
  // if unparseable so the wheel picker always has SOME meaningful state.
  const parsed = parsePortion(item.portion) ?? { qty: 1, unit: "serving" as CanonicalUnit, label: "serving", raw: item.portion, step: 1 };
  const currentQty = round2(parsed.qty * multiplier);

  // Effective gram weight of ONE parsed unit. Used by toGrams/fromGrams for
  // cross-category conversions in setWheelUnit. Sources, in order:
  //   1. item.grams_per_portion (AI / generic-foods explicit field — best)
  //   2. parsed from the "(~158g)" hint in portion text (legacy fallback)
  //   3. for pure-mass units, derive directly from qty (g/oz are exact)
  // We divide by parsed.qty because grams_per_portion is the gram weight
  // of the WHOLE portion ("2 cups (280g)" → 280), and toGrams() expects
  // grams per ONE of the unit.
  const rawGrams = item.grams_per_portion ?? parseGramsFromPortion(item.portion) ?? undefined;
  let gramsPerUnit: number | null = null;
  if (rawGrams != null && parsed.qty > 0) {
    gramsPerUnit = rawGrams / parsed.qty;
  } else if (parsed.unit === "g") {
    gramsPerUnit = 1;
  } else if (parsed.unit === "oz") {
    gramsPerUnit = 28.3495;
  }
  // Density for the food's category. Falls back to a name-based guess when
  // the AI didn't tag food_category, and to 1.0 (water-like) when even
  // that's ambiguous.
  const category = item.food_category ?? guessFoodCategory(item.name);
  const density = densityFor(category);
  const hasGrams = gramsPerUnit != null;

  // Split the displayed quantity into integer + fraction parts for the wheel.
  // For mass / volume units we collapse the fraction column.
  const isMassVol = isMassOrVolumeUnit(parsed.unit);

  // Quantize for the wheel picker. For mass: find the nearest value in
  // MASS_VALUES (do NOT force a 5g minimum — preserve the actual quantity).
  let wheelInt: number;
  if (isMassVol) {
    // Find the closest MASS_VALUE to current quantity.
    let closest = MASS_VALUES[0];
    let bestDiff = Math.abs(currentQty - closest);
    for (const v of MASS_VALUES) {
      const d = Math.abs(currentQty - v);
      if (d < bestDiff) {
        bestDiff = d;
        closest = v;
      }
    }
    wheelInt = closest;
  } else {
    wheelInt = Math.floor(currentQty);
  }
  const wheelFracValue = isMassVol ? 0 : round2(currentQty - Math.floor(currentQty));
  const wheelFracKey =
    FRACTION_VALUES.find((f) => Math.abs(f.value - wheelFracValue) < 0.02)?.key ?? "0";

  function setWheelInt(newInt: number) {
    if (isMassVol) {
      if (newInt > 0) onUpdateMultiplier(newInt / parsed.qty);
      return;
    }
    // Count-based: combine int + current fraction. If user scrolls int to 0
    // with no fraction set (i.e. trying to express "less than one"), snap
    // the fraction to ½ so the displayed value becomes 0.5 instead of
    // silently failing. Previously the wheel just bounced back to the prior
    // value and users assumed fractions weren't supported.
    let newQty = newInt + wheelFracValue;
    if (newQty <= 0) newQty = 0.5;
    onUpdateMultiplier(newQty / parsed.qty);
  }

  function setWheelFrac(fracKey: string) {
    const frac = FRACTION_VALUES.find((f) => f.key === fracKey)?.value ?? 0;
    // Use wheelInt (the current displayed integer), not Math.floor(currentQty)
    // — the user just scrolled the integer column and the displayed value
    // might already be ahead of the not-yet-committed currentQty.
    let newQty = Math.floor(currentQty) + frac;
    // If they zeroed the fraction while the integer is also 0, bump to 1
    // (the "no, I meant a whole one" case) rather than silently rejecting.
    if (newQty <= 0) newQty = 1;
    onUpdateMultiplier(newQty / parsed.qty);
  }

  function setWheelUnit(newUnit: CanonicalUnit) {
    if (newUnit === parsed.unit) return;

    // Path 1: same-category conversion (cup ↔ tbsp, g ↔ oz). Pure ratio
    // math, no density / grams data needed. Macros stay correct because
    // the food amount is identical.
    const factor = unitConversionFactor(parsed.unit, newUnit);
    if (factor !== null) {
      const convertedQty = currentQty * factor;
      const wheelMax = isMassOrVolumeUnit(newUnit) ? 500 : 100;
      if (convertedQty > 0 && convertedQty <= wheelMax) {
        const newPortion = formatPortion(convertedQty, newUnit);
        onUpdateItem({ portion: newPortion });
        onUpdateMultiplier(1);
        return;
      }
    }

    // Path 2: cross-category via grams. If we know the gram weight of one
    // parsed unit (gramsPerUnit) AND we have a density estimate for the
    // food's category, we can round-trip through grams as the canonical
    // middle to convert any unit to any other unit. The food amount in
    // grams stays constant across the swap, so macros remain accurate.
    if (gramsPerUnit != null) {
      const currentGrams = toGrams(currentQty, parsed.unit, {
        density,
        gramsPerPortion: gramsPerUnit
      });
      if (currentGrams != null) {
        const newQty = fromGrams(currentGrams, newUnit, {
          density,
          gramsPerPortion: gramsPerUnit
        });
        const wheelMax = isMassOrVolumeUnit(newUnit) ? 1000 : 100;
        if (newQty != null && newQty > 0 && newQty <= wheelMax) {
          const newPortion = formatPortion(newQty, newUnit);
          onUpdateItem({ portion: newPortion });
          onUpdateMultiplier(1);
          // Macros unchanged — gram-equivalent food amount preserved.
          return;
        }
      }
    }

    // Path 3: defensive fallback. Wheel UI should never offer a unit this
    // can't reach via Path 1 or 2, but if it ever does we rebase to
    // "1 newUnit" without zeroing macros — manual correction is better
    // than data loss.
    const newPortion = formatPortion(1, newUnit);
    onUpdateItem({ portion: newPortion });
    onUpdateMultiplier(1);
  }

  function setQuickMultiplier(q: number) {
    onUpdateMultiplier(q);
  }

  const collapsedSummary = `${currentQty} ${UNIT_LABELS[parsed.unit]} · ${Math.round(item.calories)} kcal`;

  const dotColor = colorForName(item.name);

  return (
    <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
      {/* Collapsed row — always tappable */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-slate-800/40"
        aria-expanded={expanded}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="text-slate-100 font-medium truncate">{item.name}</div>
          <div className="text-xs text-slate-400 tabular-nums mt-0.5 truncate">
            {collapsedSummary}
            <span className="text-slate-500"> · </span>
            <span className="text-sky-400">P</span> {item.protein_g.toFixed(0)}
            <span className="text-slate-500"> · </span>
            <span className="text-amber-400">C</span> {item.carbs_g.toFixed(0)}
            <span className="text-slate-500"> · </span>
            <span className="text-violet-400">F</span> {item.fat_g.toFixed(0)}
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-500 transition-transform flex-shrink-0 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-slate-800 px-4 py-4 space-y-4 bg-slate-950/40">
          {/* Editable name */}
          <input
            value={item.name}
            onChange={(e) => onUpdateItem({ name: e.target.value })}
            className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-emerald-500"
            aria-label="Item name"
          />

          {/* Wheel picker — 3 columns (int / fraction / unit), with the
              fraction column hidden when the unit is mass/volume. */}
          <div className="text-[11px] text-slate-500 text-center -mb-1">
            Swipe each column to set quantity · fraction · unit
          </div>
          <WheelPicker
            columns={
              [
                {
                  key: "int",
                  values: isMassVol ? MASS_VALUES : INT_VALUES,
                  value: wheelInt,
                  onChange: (v: string | number) => setWheelInt(Number(v)),
                  ariaLabel: "Quantity"
                } as WheelColumn<string | number>,
                !isMassVol &&
                  ({
                    key: "frac",
                    // FRACTION_KEYS is a stable module-level reference — do
                    // NOT inline FRACTION_VALUES.map() here, that creates a
                    // new array each render and triggers WheelPicker's sync.
                    values: FRACTION_KEYS,
                    value: wheelFracKey,
                    onChange: (v: string | number) => setWheelFrac(String(v)),
                    render: (v: string | number) =>
                      FRACTION_VALUES.find((f) => f.key === String(v))?.label ?? "—",
                    ariaLabel: "Fraction"
                  } as WheelColumn<string | number>),
                {
                  key: "unit",
                  // With grams_per_portion + density we can route any
                  // unit conversion through grams as canonical middle —
                  // wheel offers ALL units. Without it we fall back to
                  // same-category-only switching. See getCompatibleUnits().
                  values: getCompatibleUnits(parsed.unit, hasGrams),
                  value: parsed.unit,
                  onChange: (v: string | number) => setWheelUnit(v as CanonicalUnit),
                  render: (v: string | number) =>
                    UNIT_LABELS[v as CanonicalUnit] ?? String(v),
                  ariaLabel: "Unit"
                } as WheelColumn<string | number>
              ].filter(Boolean) as WheelColumn<string | number>[]
            }
          />

          {/* Quick-set chips — for "I had double" without scrolling the wheel */}
          <div className="flex gap-1.5">
            {QUICK_QTYS.map((q) => {
              const isActive = Math.abs(multiplier - q) < 0.01;
              return (
                <button
                  type="button"
                  key={q}
                  onClick={() => setQuickMultiplier(q)}
                  className={`flex-1 py-1.5 rounded-lg text-xs tabular-nums ${
                    isActive
                      ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40 font-semibold"
                      : "bg-slate-800 text-slate-400"
                  }`}
                >
                  {q}×
                </button>
              );
            })}
          </div>

          {/* Custom portion escape hatch — surfaces when the wheel doesn't
              expose the unit/quantity the user wants (e.g. "1 container",
              "half a sandwich", non-standard servings). Macros pin so the
              user manually corrects below — that's a deliberate tradeoff
              for the flexibility this enables. */}
          <div className="flex items-center justify-center gap-3 pt-0.5">
            <button
              type="button"
              onClick={() => setShowCustomPortion(true)}
              className="text-[11px] text-slate-500 hover:text-emerald-400 underline-offset-2 hover:underline"
            >
              Custom portion…
            </button>
            <span className="text-slate-700">·</span>
            <button
              type="button"
              onClick={() => setShowReport(true)}
              className="text-[11px] text-slate-500 hover:text-rose-400 underline-offset-2 hover:underline"
              title="Tell us if these macros look wrong"
            >
              Report incorrect
            </button>
          </div>

          {/* Tap-to-edit macros — always visible, no second toggle */}
          <div className="grid grid-cols-2 gap-2">
            <MacroCell
              label="kcal"
              value={item.calories}
              onChange={(v) => onUpdateItem({ calories: v })}
              accent="emerald"
            />
            <MacroCell
              label="protein g"
              value={item.protein_g}
              onChange={(v) => onUpdateItem({ protein_g: v })}
              accent="sky"
            />
            <MacroCell
              label="carbs g"
              value={item.carbs_g}
              onChange={(v) => onUpdateItem({ carbs_g: v })}
              accent="amber"
            />
            <MacroCell
              label="fat g"
              value={item.fat_g}
              onChange={(v) => onUpdateItem({ fat_g: v })}
              accent="violet"
            />
          </div>

          {/* Remove — intentional gesture, far from the expand affordance */}
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto block text-xs text-slate-500 hover:text-rose-400 px-2 py-1"
          >
            Remove item
          </button>
        </div>
      )}

      {showCustomPortion && (
        <PromptModal
          title="Custom portion"
          description="Type any portion — e.g. &quot;100g&quot;, &quot;1 tbsp&quot;, &quot;1/2 container&quot;. Macros won't auto-rescale; adjust the kcal/protein/carbs/fat fields below to match."
          defaultValue={item.portion}
          placeholder='e.g. "150g" or "1 tbsp"'
          confirmLabel="Set portion"
          onClose={(value) => {
            setShowCustomPortion(false);
            if (value && value.trim() && value.trim() !== item.portion) {
              onUpdateItem({ portion: value.trim() });
              // Reset multiplier to 1 so the wheel's "quantity" lines up
              // with the user's new portion baseline. Macros stay as-is.
              onUpdateMultiplier(1);
            }
          }}
        />
      )}

      {showReport && (
        <FeedbackModal
          defaultKind="bug"
          prefillBody={[
            `Incorrect macros — please review:`,
            ``,
            `Item: ${item.name}`,
            `Portion: ${item.portion}`,
            `Current values: ${Math.round(item.calories)} kcal · ${item.protein_g.toFixed(0)}g P · ${item.carbs_g.toFixed(0)}g C · ${item.fat_g.toFixed(0)}g F${item.fiber_g != null ? ` · ${item.fiber_g.toFixed(0)}g fiber` : ""}`,
            ``,
            `What looks wrong: `
          ].join("\n")}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// MacroCell — tap-to-edit numeric field. Renders as text until tapped.
// ----------------------------------------------------------------------------

function MacroCell({
  label,
  value,
  onChange,
  accent
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  accent: "emerald" | "sky" | "amber" | "violet";
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const accentClass = {
    emerald: "text-emerald-400",
    sky: "text-sky-400",
    amber: "text-amber-400",
    violet: "text-violet-400"
  }[accent];

  function commit() {
    const n = parseFloat(draft);
    if (Number.isFinite(n) && n >= 0) onChange(n);
    setEditing(false);
  }

  if (editing) {
    return (
      <label className="flex flex-col bg-slate-800/80 border border-emerald-500 rounded-lg px-3 py-2">
        <span className={`text-[10px] uppercase tracking-wide ${accentClass}`}>{label}</span>
        <input
          type="number"
          inputMode="decimal"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(String(value));
              setEditing(false);
            }
          }}
          className="bg-transparent text-slate-100 text-lg font-semibold tabular-nums focus:outline-none"
        />
      </label>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(String(value));
        setEditing(true);
      }}
      className="flex flex-col bg-slate-800/60 border border-slate-700/60 rounded-lg px-3 py-2 text-left hover:border-slate-600 active:bg-slate-800 relative"
      aria-label={`Edit ${label}`}
    >
      <Pencil
        className="w-3 h-3 text-slate-500 absolute top-2 right-2"
        aria-hidden="true"
      />
      <span className={`text-[10px] uppercase tracking-wide ${accentClass}`}>{label}</span>
      <span className="text-slate-100 text-lg font-semibold tabular-nums">{Math.round(value)}</span>
    </button>
  );
}

// ----------------------------------------------------------------------------
// utilities
// ----------------------------------------------------------------------------

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Deterministic-ish color dot from item name. Visual texture only — never
    used for any semantic meaning. */
function colorForName(name: string): string {
  const palette = [
    "bg-emerald-500",
    "bg-sky-500",
    "bg-amber-500",
    "bg-violet-500",
    "bg-rose-500",
    "bg-teal-500",
    "bg-fuchsia-500",
    "bg-orange-500"
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}
